import { observable, action } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, required, func, pattern } from 'validx'
import { generateSlugFromText } from '@/features/iam/teams/slug-util'
import { type TeamStore } from '@/features/iam/teams/TeamStore'
import { type TeamMemberStore } from '@/features/iam/teams/TeamMemberStore'
import { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Workspace } from '@/features/iam/workspaces/Workspace'

/**
 * New team state
 */
export class NewTeamModalViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor name = ''
  @observable accessor slug = ''

  readonly validation = validationContext<NewTeamModalViewModel>(this, {
    name: [
      required('Team name is required'),
      func(({ value }: { value: string }) => {
        return value.length <= 50
      }, "Name can't be more than 50 characters"),
    ],
    slug: [
      required('Slug is required'),
      func(
        ({ value }: { value: string }) => value && value.length > 2,
        'Must be longer than 2 characters',
      ),
      pattern(
        /^[a-zA-Z]+[a-zA-Z0-9-]*[a-zA-Z0-9]$/,
        'Must be alphanumerical including dashes (except at the beginning and end)',
      ),
      (opts) =>
        pattern(/([-]{2,})/g)(opts) !== true
          ? true
          : 'Consecutive dashes are not allowed',
    ],
  })

  constructor(
    private readonly _teamMemberStore: TeamMemberStore,
    private readonly _teamStore: TeamStore,
    private readonly _workspace: Workspace,
  ) {}

  @action.bound
  show() {
    this.name = ''
    this.slug = ''
    this.validation.reset()
    this.modal.show()
  }

  @action.bound
  suggestSlug() {
    if (this.slug) {
      return
    }

    this.slug = generateSlugFromText(this.name)
  }

  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Creates the team; the dialog shows a failure inline.
   */
  @action.bound
  async submit() {
    await this.createTeam().catch(() => undefined)
  }

  createTeam = task.resolved(async () => {
    if (!this.validate()) {
      return
    }
    const team = await this._teamStore.createTeam({
      name: this.name,
      slug: this.slug,
      workspaceId: this._workspace.id,
    })
    await this._teamMemberStore.refetchUserTeamMembersForWorkspace({
      userPublicId: Id.assert(team.owner.member.user.publicId),
      workspaceId: Id.assert(this._workspace.id),
    })
    this.modal.close()
  })
}
